// The University of Texas at Dallas: public college page (#2840), official
// facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - UT Dallas Common Data Set 2025-2026 (PDF served from dox.utdallas.edu,
//    report49426, linked from the Office of Institutional Success and
//    Decision Support's Common Data Set page, which is cited): C1
//    applicants, admits and enrollees for Fall 2025 with the residency
//    table, C8 test policy for Fall 2027, C21/C22 (no early decision, no
//    early action), G1/G5 2026-2027 costs (not marked as estimates).
//  - UT Dallas Office of Admission and Enrollment, Freshman Admissions:
//    Deadlines and Fees (priority December 1, regular May 1, $75 fee),
//    Criteria (holistic review, Texas Top 10%, major-specific requirements),
//    Application Process (tests not required; CLT accepted) and Timeline
//    (rolling review, four to six weeks). The deadlines page lists the fall
//    dates without naming a year; the same pages name Free College
//    Application Week as October 12 to 18, 2026, so they are this cycle's.
//    UT Dallas publishes no decision dates.
//  - UT Dallas 2026 Undergraduate Catalog (majors file).
//  - College Scorecard, school id 228787 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Cost quirk: the CDS G1 tuition is one figure, but its G4 says tuition and
// fees vary by program and 69% of full-time undergraduates pay more than
// the G1 tuition and fees. The cost note says so.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'UT Dallas Common Data Set 2025-2026',
        url: 'https://oisds.utdallas.edu/common-data-set/',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'UT Dallas Admission, Freshman Deadlines and Fees',
        url: 'https://enroll.utdallas.edu/freshman/deadlines-and-fees/',
        read: '2026-10-01',
    },
    criteria: {
        label: 'UT Dallas Admission, Freshman Criteria',
        url: 'https://enroll.utdallas.edu/freshman/criteria/',
        read: '2026-10-01',
    },
    process: {
        label: 'UT Dallas Admission, Freshman Application Process',
        url: 'https://enroll.utdallas.edu/freshman/application-process/',
        read: '2026-10-01',
    },
    timeline: {
        label: 'UT Dallas Admission, Freshman Timeline',
        url: 'https://enroll.utdallas.edu/freshman/timeline/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?228787-The-University-of-Texas-at-Dallas',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-texas-at-dallas',
    kbId: 'university_of_texas_at_dallas',
    name: 'The University of Texas at Dallas',
    shortName: 'UT Dallas',
    aliases: ['UTD'],
    location: 'Richardson, TX',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (UT Dallas brand color palette); decoration only.
    branding: {
        primary: '#E87500',
        secondary: '#154734',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_texas_at_dallas_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 36353,
        admitted: 25115,
        enrolled: 4948,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state (Texas)', applied: 28758, admitted: 20491 },
            { group: 'Out-of-state', applied: 3760, admitted: 2568 },
            { group: 'International', applied: 3835, admitted: 2056 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'UT Dallas\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'UT Dallas has no early decision or early action plan. Its December 1 priority deadline gets an earlier review for admission and scholarships, with no commitment to enroll.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'UT Dallas reviews each freshman application as a whole, for the major you name. Two rules shape where you land:',
        rows: [
            { school: 'Majors with limited space or their own requirements', requirement: 'If you are not admitted to your first-choice major because of capacity or its own requirements, UT Dallas may offer you a different major, named in your acceptance letter.' },
            { school: 'Texas Top 10%', requirement: 'Texas law admits you automatically if you graduate in the top 10% of your class at an accredited Texas high school and earn the Distinguished Level of Achievement.' },
        ],
        source: 'criteria',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular application deadline', value: 'May 1' },
        rows: [
            { plan: 'Priority deadline', deadline: 'December 1', decision: null, binding: false },
            { plan: 'Regular deadline', deadline: 'May 1', decision: null, binding: false },
        ],
        note: 'Applications open August 1 on the Common App and ApplyTexas. A file complete by December 1 gets an early review for admission and scholarships, including the AES award. UT Dallas reviews on a rolling basis and says a decision takes four to six weeks once your file is complete; it keeps reviewing files completed after May 1 but cannot promise they will be ready for fall. The application fee is $75; Texas residents who apply through ApplyTexas during Free College Application Week, October 12 to 18, 2026, pay no fee. UT Dallas does not publish fixed decision dates.',
        source: 'deadlines',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'UT Dallas does not require SAT or ACT scores for admission or scholarships. Scores you send are used only to help your review, never against you, and UT Dallas strongly encourages sending them to meet Texas Success Initiative requirements. It also takes Classic Learning Test (CLT) scores.',
        source: 'process',
        cdsLine: 'The Common Data Set marks the SAT or ACT as not required but considered if submitted for fall 2027 applicants, with scores due by August 1.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'timeline',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [10640, 36140] },
            { item: 'Required fees', amounts: [4024, 4024] },
            { item: 'Housing (on campus)', amounts: [9328, 9328] },
            { item: 'Food (on-campus meal plan)', amounts: [5906, 5906] },
            { item: 'Books and supplies', amounts: [1200, 1200] },
            { item: 'Transportation', amounts: [1748, 1748] },
            { item: 'Other expenses', amounts: [2250, 2250] },
        ],
        totals: [35096, 60596],
        note: 'For a full-time first-year student living on campus. UT Dallas\'s Common Data Set says tuition and fees vary by program, and 69% of full-time undergraduates pay more than the tuition and fees shown here.',
        source: 'cds',
        where: 'sections G1, G4 and G5',
        netPrice: {
            average: 15271,
            byIncome: [
                { income: '$0 to $30,000', amount: 10562 },
                { income: '$30,001 to $48,000', amount: 10795 },
                { income: '$48,001 to $75,000', amount: 11404 },
                { income: '$75,001 to $110,000', amount: 15098 },
                { income: 'Over $110,000', amount: 25067 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UT Dallas\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
